import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, DataTable, columnTypes, positionName,
    baseEvent, hitType, BaseRunners, dateTranslate as formatDate } from 'yankees-react-utils';
import { useOktaQueries, Player, Stats } from '../../../data';
import ReportCard from '../../../components/ReportCard';
import VideoPlayerButton from '../../../components/VideoPlayerButton';
import FieldingAngleComponent from './FieldingAngleComponent';
import StatTooltip from '../../../components/StatTooltip';

const StyledReportCard = styled(ReportCard)`
    margin-top: 10px;

    & h4 {
        text-align: left;
    }
`;

const buildQueries = (playerid, gameid) => {
    const queries = [
        {
            queryKey: ['player', playerid],
            queryFn: (t) => Player.get(t, { playerId: playerid }),
        },
        {
            queryKey: ['fplayLogs', playerid, gameid],
            queryFn: (t) => Stats.fplayLogs(t, { playerid, gameid }),
        },
    ];
    return queries;
};

const angleColumn = (breakdown_angle) => {
    if (breakdown_angle === null) return '--';
    return <FieldingAngleComponent angle={breakdown_angle} />;
};

const probabilityColumn = (hasstartingposition, outprob) => {
    if (hasstartingposition === true) return outprob;
    return '--';
};

const mapDataResponse = (response) => {
    const player = response[0].data || {};
    const fplaylogs = response[1].data || {};
    const ifFplaylogs = fplaylogs?.playlog.filter((p) => p.position >= 4 && p.position < 7);
    const ofFplaylogs = fplaylogs?.playlog.filter((p) => p.position >= 7 && p.position < 10);
    const firstbaseFplaylogs = fplaylogs?.playlog.filter((p) => p.position === 3);
    return { player, ifFplaylogs, ofFplaylogs, firstbaseFplaylogs };
};

const columnsIF = (translate) => [
    {
        id: 'video',
        title: translate('sections.post-game.fielding.video'),
        type: columnTypes.NODE,
        transform: (x) => <VideoPlayerButton gameid={x.gameid} eventseq={x.eventseq} pitchseq={x.pitchseq} />,
    },
    { id: 'gamedate', title: translate('common.date'), transform: (row) => (formatDate(translate, row.gamedate) || row.yearid), type: columnTypes.TEXT },
    { id: 'levelabbr', title: translate('common.abbr-level'), type: columnTypes.TEXT },
    { id: 'position', title: translate('common.abbr-position'), type: columnTypes.TEXT, transform: (x) => positionName(x.position, translate) },
    {
        id: 'inning',
        title: translate('baseball.abbr-inning'),
        type: columnTypes.TEXT,
        transform: (x) => <>{x.istop ? 'Top' : 'Bot'}&nbsp;{x.inning}</>,
    },
    {
        id: 'batter',
        title: translate('baseball.batter'),
        type: columnTypes.TEXT,
        transform: (x) => <>{x.batterusesname}&nbsp;{x.batterlastname}</>,
    },
    { id: 'outsbefore', title: translate('baseball.outs_other') },
    {
        id: 'runnerson',
        title: translate('common.abbr-runnerson'),
        type: columnTypes.NODE,
        transform: (x) => <BaseRunners baseState={x.bcbefore} emptyColor={x.idx % 2 === 0 ? 'white' : undefined} height={20} width={30} />,
    },
    { id: 'baseevent', title: translate('common.result'), transform: (x) => baseEvent(x.baseevent, false, undefined, translate), type: columnTypes.TEXT },
    {
        id: 'hittype',
        title: translate('sections.post-game.fielding.hittype'),
        transform: (x) => hitType(x.hittype, false, translate),
        type: columnTypes.TEXT,
    },
    { id: 'hit_initspeed', title: () => (<StatTooltip statistic="ExitVelo">{translate('sections.post-game.fielding.exitvelo')}</StatTooltip>), precision: 0, type: columnTypes.NUMBER },
    { id: 'hit_vertangle', title: () => (<StatTooltip statistic="LaunchAngle">{translate('sections.post-game.fielding.launchangle')}</StatTooltip>), precision: 0, type: columnTypes.NUMBER },
    {
        id: 'totalprob',
        title: () => (<StatTooltip statistic="TotalOutProbability">{translate('sections.post-game.fielding.outprob')}</StatTooltip>),
        type: columnTypes.PERCENT,
        transform: (x) => probabilityColumn(x.hasstartingposition, x.totalprob),
    },
    {
        id: 'firststeptime',
        title: () => (<StatTooltip statistic="FirstStepIFPlay">{translate('sections.post-game.fielding.1ststeptime')}</StatTooltip>),
        type: columnTypes.TEXT,
        transform: ({ firststeptime, maxeffort }) => <>{(maxeffort && firststeptime) ? parseFloat(firststeptime.toFixed(2)) : '--'}</>,
    },
    { id: 'throwvelo', title: () => (<StatTooltip statistic="ThrowVelocityPlay">{translate('sections.post-game.fielding.throwvelo')}</StatTooltip>), transform: ({ throwvelo }) => throwvelo, type: columnTypes.NUMBER },
];

const columnsOF = (translate) => [
    {
        id: 'video',
        title: translate('sections.post-game.fielding.video'),
        type: columnTypes.NODE,
        transform: (x) => <VideoPlayerButton gameid={x.gameid} eventseq={x.eventseq} pitchseq={x.pitchseq} />,
    },
    { id: 'gamedate', title: translate('common.date'), transform: (row) => (formatDate(translate, row.gamedate) || row.yearid), type: columnTypes.TEXT },
    { id: 'levelabbr', title: translate('common.abbr-level'), type: columnTypes.TEXT },
    { id: 'position', title: translate('common.abbr-position'), type: columnTypes.TEXT, transform: (x) => positionName(x.position) },
    {
        id: 'inning',
        title: translate('baseball.abbr-inning'),
        type: columnTypes.TEXT,
        transform: (x) => <>{x.istop ? 'Top' : 'Bot'}&nbsp;{x.inning}</>,
    },
    {
        id: 'batter',
        title: translate('baseball.batter'),
        type: columnTypes.TEXT,
        transform: (x) => <>{x.batterusesname}&nbsp;{x.batterlastname}</>,
    },
    { id: 'outsbefore', title: translate('baseball.outs_other') },
    {
        id: 'runnerson',
        title: translate('common.abbr-runnerson'),
        type: columnTypes.NODE,
        transform: (x) => <BaseRunners baseState={x.bcbefore} emptyColor={x.idx % 2 === 0 ? 'white' : undefined} height={20} width={30} />,
    },
    { id: 'baseevent', title: translate('common.result'), transform: (x) => baseEvent(x.baseevent, false, undefined, translate), type: columnTypes.TEXT },
    { id: 'hittype', title: translate('sections.post-game.fielding.hittype'), transform: (x) => hitType(x.hittype, false, translate), type: columnTypes.TEXT },
    { id: 'hit_initspeed', title: () => (<StatTooltip statistic="ExitVelo">{translate('sections.post-game.fielding.exitvelo')}</StatTooltip>), precision: 0, type: columnTypes.NUMBER },
    { id: 'hit_vertangle', title: () => (<StatTooltip statistic="LaunchAngle">{translate('sections.post-game.fielding.launchangle')}</StatTooltip>), precision: 0, type: columnTypes.NUMBER },
    {
        id: 'catchprobability',
        title: () => (<StatTooltip statistic="FieldingCatchProbability">{translate('sections.post-game.fielding.catchprob')}</StatTooltip>),
        precision: 0,
        type: columnTypes.PERCENT,
        transform: (x) => probabilityColumn(x.hasstartingposition, x.catchprobability),
    },
    {
        id: 'breakdown_angle',
        title: translate('sections.post-game.fielding.anglebreakdown'),
        type: columnTypes.NODE,
        transform: ({ breakdown_angle }) => angleColumn(breakdown_angle),
    },
    {
        id: 'firststeptime',
        title: () => (<StatTooltip statistic="FirstStepOFPlay">{translate('sections.post-game.fielding.1ststeptime')}</StatTooltip>),
        type: columnTypes.TEXT,
        transform: ({ firststeptime, maxeffort }) => <>{(maxeffort && firststeptime) ? parseFloat(firststeptime.toFixed(2)) : '--'}</>,
    },
    {
        id: 'acceleration',
        title: () => (<StatTooltip statistic="AccelerationPlay">{translate('sections.post-game.fielding.acceleration')}</StatTooltip>),
        type: columnTypes.TEXT,
        transform: ({ acceleration, maxeffort }) => <>{(maxeffort && acceleration) ? parseFloat(acceleration.toFixed(2)) : '--'}</>,
    },
    { id: 'throwvelo', title: () => (<StatTooltip statistic="ThrowVelocityPlay">{translate('sections.post-game.fielding.throwvelo')}</StatTooltip>), transform: ({ throwvelo }) => throwvelo, type: columnTypes.NUMBER, precision: 0 },
    { id: 'startingdepth', title: () => (<StatTooltip statistic="StatcastDepth">{translate('sections.post-game.fielding.startingdepth')}</StatTooltip>), transform: ({ startingdepth }) => startingdepth, type: columnTypes.NUMBER },
    { id: 'distcovered', title: () => (<StatTooltip statistic="FieldingDistanceCovered">{translate('sections.post-game.fielding.distancecovered')}</StatTooltip>), transform: ({ distcovered }) => distcovered, type: columnTypes.NUMBER },
    { id: 'hangtime', title: () => (<StatTooltip statistic="Hangtime">{translate('sections.post-game.fielding.hangtime')}</StatTooltip>), precision: 1, type: columnTypes.NUMBER },
];

const columns1B = (translate) => [
    {
        id: 'video',
        title: translate('sections.post-game.fielding.video'),
        type: columnTypes.NODE,
        transform: (x) => <VideoPlayerButton gameid={x.gameid} eventseq={x.eventseq} pitchseq={x.pitchseq} />,
    },
    { id: 'gamedate', title: translate('common.date'), transform: (row) => (formatDate(translate, row.gamedate) || row.yearid), type: columnTypes.TEXT },
    { id: 'levelabbr', title: translate('common.abbr-level'), type: columnTypes.TEXT },
    { id: 'position', title: translate('common.abbr-position'), type: columnTypes.TEXT, transform: (x) => positionName(x.position) },
    {
        id: 'inning',
        title: translate('baseball.abbr-inning'),
        type: columnTypes.TEXT,
        transform: (x) => <>{x.istop ? 'Top' : 'Bot'}&nbsp;{x.inning}</>,
    },
    {
        id: 'batter',
        title: translate('baseball.batter'),
        type: columnTypes.TEXT,
        transform: (x) => <>{x.batterusesname}&nbsp;{x.batterlastname}</>,
    },
    { id: 'outsbefore', title: translate('baseball.outs_other') },
    {
        id: 'runnerson',
        title: translate('common.abbr-runnerson'),
        type: columnTypes.NODE,
        transform: (x) => <BaseRunners baseState={x.bcbefore} emptyColor={x.idx % 2 === 0 ? 'white' : undefined} height={20} width={30} />,
    },
    { id: 'baseevent', title: translate('common.result'), transform: (x) => baseEvent(x.baseevent, false, undefined, translate), type: columnTypes.TEXT },
    { id: 'hittype', title: translate('sections.post-game.fielding.hittype'), transform: (x) => hitType(x.hittype, false, translate), type: columnTypes.TEXT },
    { id: 'hit_initspeed', title: () => (<StatTooltip statistic="ExitVelo">{translate('sections.post-game.fielding.exitvelo')}</StatTooltip>), precision: 0, type: columnTypes.NUMBER },
    { id: 'hit_vertangle', title: () => (<StatTooltip statistic="LaunchAngle">{translate('sections.post-game.fielding.launchangle')}</StatTooltip>), precision: 0, type: columnTypes.NUMBER },
    {
        id: 'totalprob',
        title: () => (<StatTooltip statistic="TotalOutProbability">{translate('sections.post-game.fielding.outprob')}</StatTooltip>),
        type: columnTypes.PERCENT,
        transform: (x) => probabilityColumn(x.hasstartingposition, x.totalprob),
    },
    {
        id: 'firststeptime',
        title: () => (<StatTooltip statistic="FirstStep">{translate('sections.post-game.fielding.1ststeptime')}</StatTooltip>),
        type: columnTypes.TEXT,
        transform: ({ firststeptime, maxeffort }) => <>{(maxeffort && firststeptime) ? parseFloat(firststeptime.toFixed(2)) : '--'}</>,
    },
];

const Fielding = ({ playerid, gameid }) => {
    const { t: translate } = useTranslation();
    const dataQueries = buildQueries(playerid, gameid);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (dataQueriesResponse.some((q) => q.isLoading)) {
        return <LoadingSpinner />;
    }

    const { ifFplaylogs, ofFplaylogs, firstbaseFplaylogs } = mapDataResponse(dataQueriesResponse, gameid);

    return (
        <>

            {ifFplaylogs.length > 0 && (
                <>
                    <StyledReportCard title={translate('sections.post-game.fielding.if-header')}>
                        <DataTable columns={columnsIF(translate)} rows={ifFplaylogs} />
                    </StyledReportCard>

                </>
            )}
            {ofFplaylogs.length > 0 && (
                <>
                    <StyledReportCard title={translate('sections.post-game.fielding.of-header')}>
                        <DataTable columns={columnsOF(translate)} rows={ofFplaylogs} />
                    </StyledReportCard>

                </>
            )}
            {firstbaseFplaylogs.length > 0 && (
                <>
                    <StyledReportCard title={translate('sections.post-game.fielding.1b-header')}>
                        <DataTable columns={columns1B(translate)} rows={firstbaseFplaylogs} />
                    </StyledReportCard>

                </>
            )}
            {ifFplaylogs.length === 0 && ofFplaylogs.length === 0 && firstbaseFplaylogs.length === 0 && (
                <>
                    <StyledReportCard title={translate('sections.post-game.fielding.nodata-header')}>
                        <span>{translate('sections.post-game.fielding.nodata-body')}</span>
                    </StyledReportCard>
                </>
            )}
        </>
    );
};

Fielding.propTypes = {
    playerid: PropTypes.number.isRequired,
    gameid: PropTypes.number.isRequired,
};

export default Fielding;
